'use client'

import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'
import clsx from 'clsx'
import { useState } from 'react'

const FaqItem = ({ question, answer, citations, isLastItem, index }) => {
  const [isOpen, setIsOpen] = useState(false)
  const toggleFaq = () => setIsOpen(!isOpen)

  const EXACTLY_INVALID_URIS = ['null', 'undefined', 'none']
  const CONTAINS_INVALID_URIS = [
    'none available',
    'no specific source',
    'summary provided',
    'no specific citation',
    'summary article',
    'no specific uri',
    'not directly cited',
  ]

  const safeCitations = (
    Array.isArray(citations)
      ? citations
      : citations && typeof citations === 'object'
        ? [citations]
        : []
  ).filter((citation) => {
    const name = citation?.name?.trim()
    const uri = citation?.uri?.toLowerCase()?.trim()

    if (!name || !uri) return false
    if (EXACTLY_INVALID_URIS.includes(uri)) return false
    return !CONTAINS_INVALID_URIS.some((phrase) => uri.includes(phrase))
  })

  const answerId = `faq-answer-${index}`

  return (
    <div className="w-full">
      <dt>
        <button
          id={`faq-question-${index}`}
          aria-controls={answerId}
          aria-expanded={isOpen}
          className="group w-full text-left py-5 flex justify-between items-start gap-4 hover:bg-muted/30 transition-colors px-4 -mx-4"
          onClick={toggleFaq}
        >
          <span className="font-serif text-base md:text-lg font-semibold text-foreground leading-snug">
            {question}
          </span>
          {isOpen ? (
            <ChevronUpIcon className="mt-1 text-muted-foreground w-5 h-5 flex-shrink-0 transition-transform group-hover:text-foreground" />
          ) : (
            <ChevronDownIcon className="mt-1 text-muted-foreground w-5 h-5 flex-shrink-0 transition-transform group-hover:text-foreground" />
          )}
        </button>
      </dt>

      <dd
        id={answerId}
        aria-labelledby={`faq-question-${index}`}
        className={clsx(
          'transition-all duration-300 ease-in-out overflow-hidden',
          isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
        )}
      >
        <div className="pb-6 px-4 space-y-4">
          <p className="text-sm md:text-base text-foreground font-serif leading-relaxed">
            {answer}
          </p>
          {safeCitations.length > 0 && (
            <div className="pt-2 border-t border-border">
              <p className="byline mb-3">Sources:</p>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {safeCitations.map((citation, idx) => (
                  <a
                    key={idx}
                    href={citation.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs text-foreground hover:text-muted-foreground underline underline-offset-2 transition-colors"
                  >
                    {citation?.preview?.favicon && (
                      <img
                        className="w-4 h-4 rounded-sm border border-border"
                        src={citation.preview.favicon}
                        alt={citation.name}
                        aria-hidden="true"
                      />
                    )}
                    <span className="font-sans">{citation.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </dd>
    </div>
  )
}

export default FaqItem
